/**
 * LivePreviewCard Component
 *
 * Renders a mock comparison result card using the current branding values.
 * Demonstrates all branding colors, fonts, and logo in a realistic layout.
 * Updates in real-time as branding values change.
 */

import type { BrandingColors, BrandingFonts } from "@tatvex/config";
import type { ReactElement } from "react";

// ============================================================================
// Types
// ============================================================================

interface LivePreviewCardProps {
  /** Branding colors to apply */
  readonly colors: BrandingColors;
  /** Branding fonts to apply */
  readonly fonts?: BrandingFonts | undefined;
  /** Logo URL to display */
  readonly logoUrl?: string | undefined;
}

// ============================================================================
// Component
// ============================================================================

export function LivePreviewCard({ colors, fonts, logoUrl }: LivePreviewCardProps): ReactElement {
  const fontHeading = fonts?.heading
    ? `"${fonts.heading}", system-ui, sans-serif`
    : "system-ui, sans-serif";
  const fontBody = fonts?.body ? `"${fonts.body}", system-ui, sans-serif` : "system-ui, sans-serif";

  return (
    <div
      className="rounded-lg border p-6"
      style={{ backgroundColor: colors.background, borderColor: `${colors.textSecondary}33` }}
      data-testid="live-preview"
    >
      {/* Header with logo */}
      <div className="mb-4 flex items-center gap-3">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Brand logo"
            className="h-8 w-auto max-w-[120px] object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div
            className="flex h-8 w-8 items-center justify-center rounded"
            style={{ backgroundColor: colors.primary }}
          >
            <span style={{ color: "#FFFFFF", fontSize: "14px", fontWeight: "bold" }}>T</span>
          </div>
        )}
        <span
          style={{
            color: colors.textPrimary,
            fontFamily: fontHeading,
            fontWeight: 600,
            fontSize: "16px",
          }}
        >
          Compare & Save
        </span>
      </div>

      {/* Mock comparison card */}
      <div
        className="rounded-lg border p-4"
        style={{
          backgroundColor: colors.surface,
          borderColor: `${colors.textSecondary}22`,
        }}
        data-testid="preview-card"
      >
        {/* Product name */}
        <h3
          style={{
            color: colors.textPrimary,
            fontFamily: fontHeading,
            fontWeight: 600,
            fontSize: "18px",
            margin: 0,
            marginBottom: "4px",
          }}
        >
          Premium Plan
        </h3>

        {/* Description */}
        <p
          style={{
            color: colors.textSecondary,
            fontFamily: fontBody,
            fontSize: "14px",
            margin: 0,
            marginBottom: "12px",
            lineHeight: "1.5",
          }}
        >
          Our most popular plan with comprehensive coverage and premium features for your needs.
        </p>

        {/* Accent highlight badge */}
        <div className="mb-3 flex items-center gap-2">
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            style={{
              backgroundColor: `${colors.accent}15`,
              color: colors.accent,
              border: `1px solid ${colors.accent}30`,
            }}
            data-testid="preview-accent-badge"
          >
            Best Value
          </span>
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            style={{
              backgroundColor: `${colors.primary}15`,
              color: colors.primary,
            }}
          >
            Top Rated
          </span>
        </div>

        {/* Feature list */}
        <div className="mb-4 flex flex-col gap-1.5">
          {["Unlimited access", "Priority support", "Advanced analytics"].map((feature) => (
            <div key={feature} className="flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                <circle cx="7" cy="7" r="7" fill={`${colors.primary}20`} />
                <path
                  d="M4 7L6 9L10 5"
                  stroke={colors.primary}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                style={{
                  color: colors.textSecondary,
                  fontFamily: fontBody,
                  fontSize: "13px",
                }}
              >
                {feature}
              </span>
            </div>
          ))}
        </div>

        {/* Price and CTA */}
        <div className="flex items-center justify-between">
          <div>
            <span
              style={{
                color: colors.textPrimary,
                fontFamily: fontHeading,
                fontWeight: 700,
                fontSize: "24px",
              }}
            >
              $49.99
            </span>
            <span
              style={{
                color: colors.textSecondary,
                fontFamily: fontBody,
                fontSize: "13px",
                marginLeft: "4px",
              }}
            >
              /month
            </span>
          </div>
          <button
            type="button"
            className="rounded-md px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90"
            style={{
              backgroundColor: colors.primary,
              color: "#FFFFFF",
              fontFamily: fontBody,
            }}
            data-testid="preview-cta-button"
          >
            Get Started
          </button>
        </div>
      </div>

      {/* Status colors preview */}
      {(colors.error || colors.success || colors.warning) && (
        <div className="mt-4 flex items-center gap-3">
          {colors.success && (
            <span
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium"
              style={{ backgroundColor: `${colors.success}15`, color: colors.success }}
            >
              Available
            </span>
          )}
          {colors.warning && (
            <span
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium"
              style={{ backgroundColor: `${colors.warning}15`, color: colors.warning }}
            >
              Limited
            </span>
          )}
          {colors.error && (
            <span
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium"
              style={{ backgroundColor: `${colors.error}15`, color: colors.error }}
            >
              Unavailable
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export type { LivePreviewCardProps };
